/**
 * BOTTA — global scripts.
 * Custom elements and progressive enhancement on top of plain Shopify forms.
 */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  /* ---------- Toast ---------- */
  let toastTimer;

  function hideToast() {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.classList.remove('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 250);
  }

  function showToast(message, isError = false) {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.querySelector('[data-toast-message]').textContent = message;
    toast.querySelector('[data-toast-link]').hidden = isError;
    toast.classList.toggle('toast--error', isError);
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = setTimeout(hideToast, 4500);
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-toast-close]')) hideToast();
  });

  /* ---------- Cart ---------- */
  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count < 1;
    });
  }

  // Any form with [data-ajax-cart] adds to cart without leaving the page.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ajax-cart')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || theme.strings.cartError);

      showToast(theme.strings.added);
      fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } })
        .then((cartResponse) => cartResponse.json())
        .then((cart) => setCartCount(cart.item_count))
        .catch(() => {});
    } catch (error) {
      showToast(error.message || theme.strings.cartError, true);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = button.hasAttribute('data-sold-out');
    }
  });

  /* ---------- Cart discount codes ---------- */

  // Replaces the cart's discount codes, then reloads so Liquid shows totals and code status.
  async function setDiscountCodes(root, codes) {
    const error = root.querySelector('[data-discount-error]');
    error.hidden = true;
    root.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!response.ok) throw new Error(theme.strings.cartError);
      window.location.reload();
    } catch (err) {
      root.removeAttribute('aria-busy');
      error.textContent = err.message || theme.strings.cartError;
      error.hidden = false;
    }
  }

  function currentDiscountCodes(root) {
    return [...root.querySelectorAll('[data-discount-remove]')].map((button) => button.dataset.discountRemove);
  }

  function applyDiscountCode(root) {
    const input = root.querySelector('[data-discount-input]');
    const code = input.value.trim().toUpperCase();
    if (!code) {
      input.focus();
      return;
    }
    const codes = currentDiscountCodes(root).filter((existing) => existing.toUpperCase() !== code);
    setDiscountCodes(root, [...codes, code]);
  }

  document.addEventListener('click', (event) => {
    const root = event.target.closest('[data-cart-discount]');
    if (!root) return;

    if (event.target.closest('[data-discount-apply]')) {
      applyDiscountCode(root);
      return;
    }

    const remove = event.target.closest('[data-discount-remove]');
    if (remove) {
      const codes = currentDiscountCodes(root).filter((code) => code !== remove.dataset.discountRemove);
      setDiscountCodes(root, codes);
    }
  });

  // Enter in the code field applies it instead of submitting the surrounding cart form.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-discount-input]')) return;
    event.preventDefault();
    applyDiscountCode(event.target.closest('[data-cart-discount]'));
  });

  // Cart page: submit quantity changes automatically.
  let cartUpdateTimer;
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-auto-submit]')) {
      input.form?.submit();
      return;
    }
    const form = input.closest('form[data-cart-form]');
    if (!form || !input.matches('input[name="updates[]"]')) return;
    clearTimeout(cartUpdateTimer);
    cartUpdateTimer = setTimeout(() => {
      form.classList.add('is-loading');
      form.submit();
    }, 500);
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const button = event.target.closest('button[name]');
      const input = this.querySelector('input');
      if (!button || !input) return;
      event.preventDefault();

      const min = Number(input.min) || 0;
      const max = input.max ? Number(input.max) : Infinity;
      const current = Number(input.value) || min;
      const next = Math.min(max, Math.max(min, current + (button.name === 'plus' ? 1 : -1)));
      if (next === current) return;

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
  }

  define('quantity-input', QuantityInput);

  /* ---------- Product form (variant picker) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    onChange = (event) => {
      if (!event.target.matches('[data-option-input]')) return;

      const groups = [...this.querySelectorAll('[data-option]')];
      const selected = groups.map((group) => group.querySelector('input:checked')?.value);
      groups.forEach((group, index) => {
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = selected[index] ?? '';
      });

      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, index) => value === selected[index])
      );
      this.render(variant);
    };

    render(variant) {
      const button = this.querySelector('[data-add-button]');
      const label = this.querySelector('[data-add-label]');
      const available = Boolean(variant?.available);

      if (button) {
        button.toggleAttribute('data-sold-out', !available);
        button.disabled = !available;
      }
      if (label) {
        label.textContent = !variant
          ? theme.strings.unavailable
          : available
            ? theme.strings.addToCart
            : theme.strings.soldOut;
      }
      if (!variant) return;

      const idInput = this.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;

      // Mirrors snippets/price.liquid.
      const price = this.querySelector('[data-price]');
      if (price) {
        price.classList.toggle('price--sale', Boolean(variant.compare));
        price.innerHTML =
          `<span class="price__current">${variant.price}</span>` +
          (variant.compare
            ? `<s class="price__compare">${variant.compare}</s><span class="badge">-${variant.discount}%</span>`
            : '');
      }

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (variant.media) {
        document.dispatchEvent(
          new CustomEvent('botta:variant-media', {
            detail: { section: this.dataset.section, mediaId: variant.media },
          })
        );
      }
    }
  }

  define('product-form', ProductForm);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = [...this.querySelectorAll('[data-target]')];
      if (!this.track) return;

      this.addEventListener('click', this.onThumbClick);
      document.addEventListener('botta:variant-media', this.onVariantMedia);

      if (this.thumbs.length && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.track.querySelectorAll('[data-media-id]').forEach((slide) => this.observer.observe(slide));
      }

      if (this.dataset.initial) this.goTo(this.dataset.initial, false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onThumbClick);
      document.removeEventListener('botta:variant-media', this.onVariantMedia);
      this.observer?.disconnect();
    }

    onThumbClick = (event) => {
      const thumb = event.target.closest('[data-target]');
      if (thumb) this.goTo(thumb.dataset.target);
    };

    onVariantMedia = (event) => {
      if (event.detail.section === this.dataset.section) this.goTo(event.detail.mediaId);
    };

    goTo(mediaId, smooth = true) {
      const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto',
      });
      this.setActive(mediaId);
    }

    setActive(mediaId) {
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.target === String(mediaId)));
      });
    }
  }

  define('product-gallery', ProductGallery);

  /* ---------- Mobile menu ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.desktop.addEventListener('change', this.close);
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      this.desktop?.removeEventListener('change', this.close);
    }

    close = () => {
      if (this.details) this.details.open = false;
    };

    onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.details?.open) return;
      this.close();
      this.details.querySelector('summary')?.focus();
    };

    onClick = (event) => {
      if (event.target.closest('a, [data-menu-close]')) this.close();
    };
  }

  define('menu-drawer', MenuDrawer);

  /* ---------- Hero video speed ---------- */

  // Plays the hero clip slower than real time; the rate resets on loop, so reapply it.
  function applyHeroVideoSpeed(root = document) {
    root.querySelectorAll('[data-hero-video]').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const speed = Number(wrapper.dataset.speed) / 100;
      if (!video || !speed || speed === 1) return;
      const setRate = () => {
        video.playbackRate = speed;
      };
      setRate();
      video.addEventListener('loadedmetadata', setRate);
      video.addEventListener('play', setRate);
    });
  }

  applyHeroVideoSpeed();
  document.addEventListener('shopify:section:load', (event) => applyHeroVideoSpeed(event.target));

  /* ---------- Header state ---------- */
  const onScroll = () => document.documentElement.classList.toggle('is-scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- Delivery estimate ---------- */
  // Turns "Entrega en 10-15 días" into real dates, counting Spanish working days from today.
  // Without JS (or on bad input) the server-rendered text stays.
  const HOLIDAYS = ['01-01', '01-06', '05-01', '08-15', '10-12', '11-01', '12-06', '12-08', '12-25'];

  function addWorkingDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      if (weekday !== 0 && weekday !== 6 && !HOLIDAYS.includes(monthDay)) added += 1;
    }
    return date;
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min);
      const max = Number(this.dataset.max);
      if (!(min > 0) || !(max >= min)) return;

      const today = new Date();
      const from = addWorkingDays(today, min);
      const to = addWorkingDays(today, max);
      const day = new Intl.DateTimeFormat('es-ES', { day: 'numeric' });
      const dayMonth = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long' });
      const start = from.getMonth() === to.getMonth() ? day.format(from) : dayMonth.format(from);

      this.textContent = `Recíbelo entre el ${start} y el ${dayMonth.format(to)}`;
    }
  }

  define('delivery-estimate', DeliveryEstimate);

  /* ---------- Sticky buy bar (phones) ---------- */
  // Shows whenever the product form's add button is off screen (below it on arrival, above it
  // after scrolling) and hides over the footer. Its button clicks the real one, so the AJAX cart is reused.
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.addButton = document.getElementById(this.dataset.form)?.querySelector('[data-add-button]');
      if (!this.addButton || !('IntersectionObserver' in window)) return;

      this.buttonOffScreen = false;
      this.overFooter = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.addButton) {
            this.buttonOffScreen = !entry.isIntersecting;
          } else {
            this.overFooter = entry.isIntersecting;
          }
        });
        const visible = this.buttonOffScreen && !this.overFooter;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.querySelector('[data-sticky-add]').tabIndex = visible ? 0 : -1;
      });
      this.observer.observe(this.addButton);
      const footer = document.querySelector('.footer, footer');
      if (footer) this.observer.observe(footer);

      this.querySelector('[data-sticky-add]').addEventListener('click', () => this.addButton.click());
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  define('sticky-buy', StickyBuy);

  /* ---------- Scroll reveal ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            });
          },
          { threshold: 0.1 }
        )
      : null;

  function observeReveal(root = document) {
    root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((element) => {
      if (revealObserver) revealObserver.observe(element);
      else element.classList.add('is-revealed');
    });
  }

  observeReveal();
  document.addEventListener('shopify:section:load', (event) => observeReveal(event.target));
})();
